import React, { MouseEvent, useEffect, useRef } from 'react';
import { Expand } from '@montana/shared/expand';


interface IFieldWrapperProps {
    label: string;
    id: string;
    children: JSX.Element | JSX.Element[];
    inFocus?: boolean;
    isValid?: boolean;
    isEmpty?: boolean;
    isTouched?: boolean;
    typeName?: string;
    disabled?: boolean;
    errorMessage?: string;
    dontRenderLabel?: boolean;
    className?: string;
    compact?: boolean;
    flex?: boolean;
    hiddenLabel?: boolean;
    inlineLabel?: boolean;
    floatingLabel?: boolean;
    bottomMargin?: boolean;
    scrollToError?: boolean | number; // Add number to enable scrolling to field more than once.
    onClick?: (event: MouseEvent<HTMLElement>) => void;
}


const BASE_CLASS = 'field-wrapper';




export const FieldWrapper: React.FC<IFieldWrapperProps> = React.memo((props: IFieldWrapperProps): JSX.Element => {

    const {
        label,
        id,
        children,
        inFocus,
        isValid,
        isEmpty,
        isTouched,
        disabled,
        typeName,
        errorMessage,
        dontRenderLabel,
        compact,
        className,
        flex,
        hiddenLabel,
        inlineLabel = (typeName !== 'checkbox' && typeName !== 'radio'),
        bottomMargin = true,
        scrollToError = false,
        onClick
    } = props;

    const ref = useRef<HTMLDivElement>(null);

    const isVisiblyValid = isValid || !isTouched;

    const buildClassName = (element = "", modifiers?: string[]): string => {
        const basis = `${BASE_CLASS}${element ? `__${element}` : ''}`;

        return modifiers
            ? `${basis} ${
                modifiers
                    .filter((modifier): boolean => !!modifier && modifier.length > 0)
                    .map((modifier): string => `${basis}--${modifier}`)
                    .join(" ")
            }`
            : basis;
    };

    const classNameModifier = (className || "").trim();

    const basicModifiers = [
        typeName || "",
        inFocus ? 'focus' : '',
        isVisiblyValid ? 'valid' : 'invalid',
        isTouched ? 'touched' : 'untouched',
        isEmpty ? 'empty' : 'filled',
        disabled ? 'disabled' : '',
        compact ? 'compact' : '',
        flex ? 'flex' : '',
        inlineLabel ? 'inline-label' : '',
        bottomMargin ? '' : 'no-margin',

        // Add the className as a modifier if it does NOT contain spaces
        classNameModifier.indexOf(" ") < 0 ? classNameModifier : '',
    ];

    const renderedLabel = !dontRenderLabel && (
        <>
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label
                htmlFor={id || ""}
                id={id ? `${id}-label` : ""}
                className={buildClassName("label", basicModifiers) + (hiddenLabel ? " visually-hidden" : "")}
            >
                { inlineLabel
                    ? <>
                        <span aria-hidden={inFocus || !isVisiblyValid} className="label-content label-content--when-not-focus">
                            {label}
                        </span>
                        <span aria-hidden={!inFocus && isVisiblyValid} className="label-content label-content--when-focus">
                            {label}
                        </span>
                    </>
                    : label
                }
            </label>
        </>
    );

    const showError = !isVisiblyValid && !!errorMessage;

    useEffect(() => {
        if (!scrollToError) {
            return;
        }

        if (showError) {
            ref.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [showError, scrollToError]);

    return (
        <div className={`${buildClassName("", basicModifiers)} ${className || ""}`.trim()} ref={ref}>
            {inlineLabel ? "" : renderedLabel}
            <div
                className={buildClassName("field", basicModifiers)}
                onClick={(event) => onClick && onClick(event)}
            >
                {
                    inlineLabel
                        ? <div className={`${buildClassName("inline", basicModifiers)} ${className || ""}`.trim()}>
                            {renderedLabel}
                            {children}
                        </div>
                        : children
                }
                {/* {isTouched && (
                    <SvgIcon
                        className={buildClassName("icon", basicModifiers)}
                        iconName={!isValid ? "alert" : "checkmark"}
                    />
                )} */}
            </div>
            <Expand active={showError}>
                <div className={buildClassName("error", basicModifiers)} role="alert">
                    <span className={buildClassName("error-text", basicModifiers)}>
                        {errorMessage}
                    </span>
                </div>
            </Expand>
            {/*{!isValid && isTouched && !!errorMessage && (
                <div className={buildClassName("error", basicModifiers)} role="alert">
                    <span className={buildClassName("error-text", basicModifiers)}>
                        {errorMessage}
                    </span>
                </div>
            )}*/}
        </div>
    );
});
